// 📖物語から新規ゲーム作成の第1段: 長編小説 → ゲームシナリオ文書（2026-07-26 指示）。
//
// 従来は 5,000 字程度の「要約済み小説」を直接①解析へ流していたが、原作の長編（日本語40万字/
// 英語100万字まで）をそのまま読み込み、ゲーム生成に必要な情報だけを 1 万トークン未満の
// シナリオ文書へまとめてから①へ渡す 2 ステップに変更した。
// 上限値・プロンプトは config/story-pipeline.json の scenario、モデル設定は config/llm.json の
// tasks.scenario で管理する（js へハードコードしない）。
//
// プロジェクト作成前に実行する工程のため、この関数はプロジェクト ID を取らない
// （保存はせず、生成したシナリオ文書を呼び出し元＝画面の編集欄へ返すだけ）。
import { callLlm, llmConfig, resolveTaskModel } from './llm-client.mjs';
import { pipelineConfig, fillTemplate } from './story-shared.mjs';
import {
    codePointLength, detectNovelGenerationLanguage, generationLanguageInstruction,
} from './story-source.mjs';

export function scenarioConfig() {
    return pipelineConfig().scenario ?? {};
}

/** 言語別の文字数上限（未定義の言語は default）。設定不正時も止めずに既定値へ倒す。 */
export function scenarioCharacterLimit(limits, language, fallback) {
    const candidate = Number(limits?.[language] ?? limits?.default);
    return Number.isFinite(candidate) && candidate > 0 ? Math.trunc(candidate) : fallback;
}

/**
 * 画面（📖物語から新規ゲーム作成）が使う上限値。
 * 上限は config を正とし、js・HTML 側へ数値を書かない（完全データ駆動）。
 */
export function scenarioUiLimits() {
    const cfg = scenarioConfig();
    const input = cfg.inputMaxCharacters ?? {};
    return {
        inputMaxCharacters: {
            ja: scenarioCharacterLimit(input, 'ja', 400000),
            default: scenarioCharacterLimit(input, 'default', 1000000),
        },
        editorMaxCharacters: Number(cfg.editorMaxCharacters) > 0 ? Math.trunc(Number(cfg.editorMaxCharacters)) : 20000,
        minSourceCharacters: Number(cfg.minSourceCharacters) > 0 ? Math.trunc(Number(cfg.minSourceCharacters)) : 200,
    };
}

/** サロゲートペアを割らずに先頭から maxCharacters 文字だけ残す。 */
function sliceCharacters(text, maxCharacters) {
    const source = String(text ?? '');
    // UTF-16 長が上限以下なら符号位置数も必ず上限以下（巨大文字列で Array.from を避ける早期判定）
    if (source.length <= maxCharacters) return source;
    return Array.from(source).slice(0, maxCharacters).join('');
}

/**
 * 入力制限を超えた小説から、はみ出した後ろ側を削除する（先頭から詰める）。
 * 物語の導入＝主人公の開始地点が書かれている部分を必ず残すため、後ろ側を捨てる。
 */
export function clampNovelForScenario(text, language = detectNovelGenerationLanguage(text)) {
    const cfg = scenarioConfig();
    const limit = scenarioCharacterLimit(cfg.inputMaxCharacters, language, 400000);
    const source = String(text ?? '');
    const sourceCharacters = codePointLength(source);
    const truncated = sourceCharacters > limit;
    return {
        text: truncated ? sliceCharacters(source, limit) : source,
        language,
        limit,
        sourceCharacters,
        usedCharacters: truncated ? limit : sourceCharacters,
        truncated,
    };
}

/**
 * 実行前に見せるコスト予測（📖 ゲームシナリオ文書作成の確認ダイアログ用）。
 * 実際に投げる本文＝入力制限で切り詰めたあとの長さで数え、モデルも callLlm と同じ解決を使う
 *（予測と実行がずれないようにする）。単価が取れないときは pricing 抜きで返し、実行自体は止めない。
 * @param {string} text 小説の全文
 * @param {{prompt?: string|number, completion?: string|number}|null} pricing OpenRouter の $/token
 */
export function estimateScenarioCost(text, pricing = null) {
    const cfg = scenarioConfig();
    const clamped = clampNovelForScenario(text);
    const perCharacter = scenarioTokensPerCharacter(clamped.language);
    const model = resolveTaskModel('scenario');
    // 出力は max_tokens が上限。予測は「最大でもここまで」を示す（実際はこれより短くなる）
    const outputTokens = Math.trunc(Number(llmConfig().tasks?.scenario?.maxTokens ?? 0)) || 0;
    const inputTokens = Math.ceil(clamped.usedCharacters * perCharacter);
    const usdPerToken = (value) => {
        const n = Number(value);
        return Number.isFinite(n) && n >= 0 ? n : null;
    };
    const promptUsd = usdPerToken(pricing?.prompt);
    const completionUsd = usdPerToken(pricing?.completion);
    const inputUsd = promptUsd === null ? null : inputTokens * promptUsd;
    const outputUsd = completionUsd === null ? null : outputTokens * completionUsd;
    return {
        model,
        language: clamped.language,
        sourceCharacters: clamped.sourceCharacters,
        usedCharacters: clamped.usedCharacters,
        droppedCharacters: clamped.sourceCharacters - clamped.usedCharacters,
        truncated: clamped.truncated,
        inputLimit: clamped.limit,
        tokensPerCharacter: perCharacter,
        inputTokens,
        outputTokens,
        targetMaxCharacters: scenarioCharacterLimit(cfg.targetMaxCharacters, clamped.language, 8000),
        inputUsd,
        outputUsd,
        totalUsd: inputUsd === null || outputUsd === null ? null : inputUsd + outputUsd,
    };
}

/** 生成言語ごとのトークン換算（日本語 1文字=1.2 / 英語ほか 1文字≒0.3）。 */
function scenarioTokensPerCharacter(language) {
    const rates = scenarioConfig().tokensPerCharacter ?? {};
    const rate = Number(rates[language] ?? rates.default);
    return Number.isFinite(rate) && rate > 0 ? rate : 1.2;
}

/** 編集欄へそのまま入れられる本文へ整える（コードフェンス・前後の空白を落とす）。 */
export function normalizeScenarioText(text) {
    let body = String(text ?? '').trim();
    const fenced = /^```[a-zA-Z0-9_-]*\n([\s\S]*?)\n?```$/.exec(body);
    if (fenced) body = fenced[1].trim();
    return body;
}

/**
 * 小説全文からゲームシナリオ文書を生成する。
 * @param {object} params
 * @param {string} params.text 小説の全文（入力制限超過分はここで切り捨てる）
 * @param {string|null} params.fileName 表示用のファイル名（プロンプトの作品名に使う）
 * @returns {Promise<{ok: true, text: string, language: string, sourceCharacters: number,
 *   usedCharacters: number, truncated: boolean, scenarioCharacters: number, clamped: boolean,
 *   costUsd: number|null, model: string}>}
 */
export async function runScenarioDraft({ text, fileName = null, onLog = () => {}, signal = null } = {}) {
    const cfg = scenarioConfig();
    const source = String(text ?? '');
    const minSource = Number(cfg.minSourceCharacters ?? 200);
    if (codePointLength(source.trim()) < minSource) {
        throw new Error(`小説の本文が短すぎます（${minSource}文字以上のテキストファイルが必要です）。`);
    }
    signal?.throwIfAborted();

    const clamped = clampNovelForScenario(source);
    const languageLabel = clamped.language === 'ja' ? '日本語' : '英語';
    onLog(`小説 ${clamped.sourceCharacters.toLocaleString()}文字を読み込みました（生成言語: ${languageLabel}）`);
    if (clamped.truncated) {
        const dropped = clamped.sourceCharacters - clamped.limit;
        onLog(`⚠ 入力制限 ${clamped.limit.toLocaleString()}文字を超えているため、後ろ側 ${dropped.toLocaleString()}文字を削除して先頭から処理します`);
    }

    const targetMax = scenarioCharacterLimit(cfg.targetMaxCharacters, clamped.language, 8000);
    onLog(`ゲームシナリオ文書を生成します（${targetMax.toLocaleString()}文字以内・開始地点とスタートマップを必ず含めます）……`);
    const prompt = fillTemplate(cfg.prompt, {
        fileName: fileName || '(無題)',
        generationLanguageInstruction: generationLanguageInstruction(clamped.language),
        targetMaxCharacters: String(targetMax),
        novelText: clamped.text,
    });
    const r = await callLlm('scenario', {
        system: cfg.system,
        prompt,
        onLog,
        // 入力へできるだけ多くの本文を詰めるため、この呼び出しでは思考を明示的に無効化する（2026-07-26 指示）。
        // 設定の取り違えで有効化されても、ここで必ず無効へ倒す
        reasoning: { enabled: false },
    });
    signal?.throwIfAborted();

    let scenario = normalizeScenarioText(r.text);
    if (!scenario) {
        throw new Error('ゲームシナリオ文書を生成できませんでした（応答が空でした）。時間をおいて再実行してください。');
    }
    // 編集欄（editorMaxCharacters）に収まることを保証する。指示を無視して長すぎた場合の歯止め
    const hardMax = Math.min(
        scenarioCharacterLimit(cfg.modelOutputMaxCharacters, clamped.language, 20000),
        Number(cfg.editorMaxCharacters) > 0 ? Math.trunc(Number(cfg.editorMaxCharacters)) : 20000,
    );
    const generatedCharacters = codePointLength(scenario);
    const overflowed = generatedCharacters > hardMax;
    if (overflowed) {
        onLog(`⚠ 生成結果が ${generatedCharacters.toLocaleString()}文字で上限 ${hardMax.toLocaleString()}文字を超えたため、末尾を切り詰めました（内容を確認してください）`);
        scenario = sliceCharacters(scenario, hardMax);
    }
    const scenarioCharacters = overflowed ? hardMax : generatedCharacters;
    onLog(`✔ ゲームシナリオ文書を生成しました（${scenarioCharacters.toLocaleString()}文字）`);

    return {
        ok: true,
        text: scenario,
        language: clamped.language,
        sourceCharacters: clamped.sourceCharacters,
        usedCharacters: clamped.usedCharacters,
        truncated: clamped.truncated,
        scenarioCharacters,
        clamped: overflowed,
        costUsd: r.costUsd ?? null,
        model: r.model,
    };
}
